import { DateTime } from 'luxon';
import { READINESS_OPTIONS } from '@sondermind/utilities/models-clinical';
import { MemberEthnicity } from './member-ethnicity';
import { PatientInsurance } from './patient-insurance';
import { IInitialCareRequest } from './care-request.model';

export class Patient {
  id: number;
  platformIdentity: string;
  userId: number;
  firstName: string;
  lastName: string;
  preferredFirstName: string;
  middleName: string;
  displayName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  homePhone: string;
  cellPhone: string;
  email: string;
  dateOfBirth: string;
  genderId: string;
  canInactivate: boolean;
  active: boolean;
  nextCalendarEventId: number;
  nextCalendarEventStartTime: string;
  nextCalendarEventEndTime: string;
  nextCalendarEventSessionType: string;
  inactivatedAt: string;
  personaClientId: number;
  nonSpnPatient: boolean;
  accountRequired: boolean;
  accountPending: boolean;
  accountInviteActive: boolean;
  // using union here to avoid compilation errors because this field can sometimes come back as "Invalid Date"
  accountInviteSentAt: Date | string;
  canSetNonSpnPatient: boolean;
  allowScheduling: boolean;
  disallowClaims: boolean;
  insurances: PatientInsurance[] = [];
  chatProfileUuid: string;
  nonClinicalChatRoomUuid: string;
  ethnicities: MemberEthnicity[];
  maritalStatusId: string;
  requiresCreditCard: boolean;
  veteranAffairs: boolean;
  patientPlatformUuid: string;
  initialCareRequest: IInitialCareRequest;

  get fullName(): string {
    let fullName = '';
    if (this.firstName) {
      fullName = this.firstName;
    }
    if (this.lastName) {
      fullName = `${fullName} ${this.lastName}`;
    }

    return fullName;
  }

  age(timezone: string = 'America/Denver'): number {
    if (!this.dateOfBirth) {
      return 0;
    }
    const dtBday = DateTime.fromISO(this.dateOfBirth, { zone: timezone });
    if (!dtBday.isValid) {
      return 0;
    }
    return Math.floor(DateTime.now().setZone(timezone).diff(dtBday, 'years').years);
  }

  isPersistent(): boolean {
    if (this.id && this.id > 0) {
      return true;
    }
    return false;
  }

  get haveClientRecord(): boolean {
    if (this.personaClientId && this.personaClientId > 0) {
      return true;
    }
    return false;
  }

  load_from_json(json: any): Patient {
    this.id = json.id;
    this.platformIdentity = json.clientPlatformIdentity;
    this.userId = json.userId;
    this.firstName = json.firstName;
    this.lastName = json.lastName;
    this.middleName = json.middleName;
    this.displayName = json.displayName;
    this.addressLine1 = json.addressLine1;
    this.addressLine2 = json.addressLine2;
    this.city = json.city;
    this.state = json.state;
    this.postalCode = json.postalCode;
    this.homePhone = json.homePhone;
    this.cellPhone = json.cellPhone;
    this.email = json.email;
    this.dateOfBirth = json.dateOfBirth;
    this.nextCalendarEventId = json.nextCalendarEventId;
    this.nextCalendarEventStartTime = json.nextCalendarEventStart;
    this.nextCalendarEventEndTime = json.nextCalendarEventEndTime;
    this.genderId = json.genderId;
    this.canInactivate = json.canInactivate;
    this.active = json.active;
    this.inactivatedAt = json.inactivatedAt;
    this.personaClientId = json.personaClientId;
    this.nonSpnPatient = json.nonSpnPatient;
    this.accountRequired = json.accountRequired;
    this.accountPending = json.accountPending;
    this.accountInviteActive = json.accountInviteActive;
    this.accountInviteSentAt = new Date(json.accountInviteSentAt);
    this.canSetNonSpnPatient = json.canSetNonSpnPatient;
    this.allowScheduling = json.allowScheduling;
    this.disallowClaims = json.disallowClaims;
    this.chatProfileUuid = json.chatProfileUuid;
    this.nonClinicalChatRoomUuid = json.nonClinicalChatRoomUuid;
    this.ethnicities = json.ethinicities || [];
    this.maritalStatusId = json.maritalStatusId;
    this.requiresCreditCard = json.requiresCreditCard;
    this.veteranAffairs = json.latestReferralVeteranAffairs;
    this.patientPlatformUuid = json.platformAccountUuid;
    if (json.nextCalendarEventId) {
      this.nextCalendarEventSessionType = json.nextCalendarEventSessionType === 'in_person' ? 'In-office' : 'Video';
    }
    this.initialCareRequest = json.initialCareRequest;

    if (json.initialCareRequest?.readinessInfo) {
      this.initialCareRequest.readinessInfo = READINESS_OPTIONS[json.initialCareRequest.readinessInfo];
    }

    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const i in json.ethnicities) {
      const ins = new MemberEthnicity();
      ins.load_from_json(json.ethnicities[i]);
      this.ethnicities.push(ins);
    }

    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    for (const i in json.patientInsurances) {
      const ins = new PatientInsurance();
      ins.load_from_json(json.patientInsurances[i]);
      this.insurances.push(ins);
    }

    return this;
  }
}
